import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Play, Pause } from "lucide-react";
import api from "@/lib/api";
import { toggleFavorite } from "@/lib/favorites";
import { usePlayer } from "@/context/PlayerContext";
import { BeatCover } from "@/components/BeatCover";

export const FavouriteBeats = () => {
  const [favs, setFavs] = useState(null);
  const { track, playing, toggle } = usePlayer();

  useEffect(() => { api.get("/me/favorites").then((r) => setFavs(r.data)).catch(() => setFavs([])); }, []);

  const unfav = (b) => toggleFavorite(b, (on) => setFavs((prev) => (on ? prev : prev.filter((x) => x.beat_id !== b.beat_id))));

  return (
    <section className="px-6 md:px-12 lg:px-20 py-8 relative z-10" data-testid="favourite-beats">
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-4 flex items-center gap-2">
        <Heart className="h-4 w-4" fill="currentColor" /> Favourite beats
      </p>
      {favs === null ? <p className="text-[#A0A0A0] text-sm">Loading...</p> : favs.length === 0 ? (
        <div className="border border-white/10 bg-[#121212] p-8 text-center" data-testid="favourite-beats-empty">
          <p className="text-[#A0A0A0] text-sm">No favourites yet. Tap the heart on any beat to save it here.</p>
          <Link to="/beats" className="mt-4 inline-block text-xs font-bold uppercase tracking-wider text-[#D90429] hover:text-white">Browse beats</Link>
        </div>
      ) : (
        <div className="border border-white/10 bg-[#121212] divide-y divide-white/5">
          {favs.map((b) => {
            const isPlaying = track?.beat_id === b.beat_id && playing;
            return (
              <div key={b.beat_id} className="flex items-center gap-4 px-4 py-3" data-testid={`favourite-${b.beat_id}`}>
                <button onClick={() => toggle(b)} className="relative h-11 w-11 shrink-0" data-testid={`favourite-play-${b.beat_id}`} aria-label="Play">
                  <BeatCover beat={b} className="h-11 w-11" />
                  <span className={`absolute inset-0 flex items-center justify-center ${isPlaying ? "bg-[#D90429]/80" : "bg-black/45 hover:bg-[#D90429]/70"}`}>
                    {isPlaying ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 ml-0.5" fill="currentColor" />}
                  </span>
                </button>
                <Link to={`/beats?beat=${b.beat_id}`} className="min-w-0 flex-1">
                  <p className="font-display font-bold truncate hover:text-[#D90429]">{b.title}</p>
                  <p className="text-[11px] uppercase tracking-wider text-[#A0A0A0] truncate">
                    {[b.bpm && `${b.bpm} BPM`, b.key, b.genre].filter(Boolean).join(" · ") || "Full stems included"}
                    {b.status === "sold" && (b.owned ? " · Owned" : " · Sold")}
                  </p>
                </Link>
                <span className="hidden sm:block font-display font-bold text-[#D90429]">RON {b.price}</span>
                <button onClick={() => unfav(b)} data-testid={`favourite-remove-${b.beat_id}`} title="Remove from favourites"
                  className="h-8 w-8 flex items-center justify-center text-[#D90429] hover:text-white transition-colors">
                  <Heart className="h-4 w-4" fill="currentColor" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
